import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import constants from "../constants/constants";
import { useLocation } from "react-router-dom";
import { faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState, useRef, useCallback } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import aboutImage from "../assets/images/about-bg.jpg";
import { useUserParams } from "../hooks/useUserParams";

const useAnimatedBorder = () => {
  const topLeftRef = useRef(null);
  const topRightRef = useRef(null);
  const bottomLeftRef = useRef(null);
  const bottomRightRef = useRef(null);
  const rightRef = useRef(null);
  const leftRef = useRef(null);

  useEffect(() => {
    const timeouts = [];

    // Top borders animation
    timeouts.push(
      setTimeout(() => {
        topLeftRef.current?.classList.remove("w-0");
        topLeftRef.current?.classList.add("w-half");
        topRightRef.current?.classList.remove("w-0");
        topRightRef.current?.classList.add("w-half");
      }, 10)
    );

    // Side borders animation
    timeouts.push(
      setTimeout(() => {
        leftRef.current?.classList.remove("h-0");
        leftRef.current?.classList.add("h-full");
        rightRef.current?.classList.remove("h-0");
        rightRef.current?.classList.add("h-full");
      }, 1000)
    );

    // Bottom borders animation
    timeouts.push(
      setTimeout(() => {
        bottomLeftRef.current?.classList.remove("w-0");
        bottomLeftRef.current?.classList.add("w-half");
        bottomRightRef.current?.classList.remove("w-0");
        bottomRightRef.current?.classList.add("w-half");
      }, 4000)
    );

    return () => timeouts.forEach((timeout) => clearTimeout(timeout));
  }, []);

  return {
    topLeftRef,
    topRightRef,
    bottomLeftRef,
    bottomRightRef,
    rightRef,
    leftRef,
  };
};

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const onWindowResize = useCallback(() => {
    setWindowSize(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, [onWindowResize]);

  return windowSize;
};

const useScrollVisibility = () => {
  const slideRefs = useRef([]);

  const addToRefs = (el) => {
    if (el && !slideRefs.current.includes(el)) {
      slideRefs.current.push(el);
    }
  };

  const isScrolledIntoView = useCallback((elem) => {
    if (elem) {
      const rect = elem.getBoundingClientRect();
      const elemTop = rect.top;
      const elemBottom = rect.bottom;
      return elemTop >= 0 && elemBottom <= window.innerHeight;
    }
    return false;
  }, []);

  useEffect(() => {
    slideRefs.current.forEach((el) => {
      if (isScrolledIntoView(el)) {
        el?.classList.remove("slide-left");
      }
    });
  }, [isScrolledIntoView]);

  return { addToRefs };
};

export default function About() {
  const windowSize = useWindowSize();
  const {
    topLeftRef,
    topRightRef,
    bottomLeftRef,
    bottomRightRef,
    rightRef,
    leftRef,
  } = useAnimatedBorder();
  const { addToRefs } = useScrollVisibility();
  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
    getCurrentHeadshot,
    getCurrentAbout,
  } = useUserParams();

  const agentName = getCurrentAgentName();
  const agentTitle = getCurrentAgentTitle();
  const agentNumber = getCurrentAgentNumber();
  const agentEmail = getCurrentAgentEmail();
  const brokerage = getCurrentBrokerage();
  const headshot = getCurrentHeadshot();
  const aboutText = getCurrentAbout() || constants.ABOUT_DESCRIPTION;

  const seoConfig = pageSEO.about();

  return (
    <>
      <SEO {...seoConfig} />
      <section
        className="relative w-full min-h-[70vh] py-16 flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${aboutImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="absolute inset-0 bg-black/10" />
        <div className="max-w-[1100px] mx-auto gap-x-24 gap-y-12 px-6 grid sm:grid-cols-2 my-12 z-10 p-10 shadow-xl backdrop-blur-lg border border-white/20 rounded-2xl">
          {/* Left: Headshot and info */}
          <div className="flex flex-col items-center">
            <div
              className="rounded-full overflow-hidden shadow-2xl mb-6 border-4"
              style={{
                borderColor: "white",
                boxShadow: `0 0 32px 0 white99`,
                transition: "box-shadow 0.3s",
              }}
            >
              <img
              src={headshot}
              alt={agentName}
                width={260}
                height={260}
                className="w-64 h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="flex flex-col gap-y-2 text-center">
              <h2
                className="text-3xl font-bold"
                style={{ color: "white" }}
              >
              {agentName}
              </h2>
              <p
                className="italic text-lg"
                style={{ color: "white" }}
              >
              {agentTitle}, {brokerage}
              </p>
              {/* Social icons with glow and hover effect */}
              <div className="flex gap-x-5 my-2 justify-center">
                {Object.entries(constants.SOCIALS)?.map(([key, value]) => (
                  <a
                    key={key}
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-transform duration-200 hover:scale-125"
                    style={{
                      filter: constants.ANIMATION_CONFIG.iconGlow
                        ? `drop-shadow(${constants.ANIMATION_CONFIG.iconGlowBlur})`
                        : undefined,
                    }}
                  >
                    <FontAwesomeIcon
                      className="fa-xl"
                      icon={constants.ICONS[key]}
                      style={{ color: "white" }}
                    />
                  </a>
                ))}
              </div>
              <p
                className="font-semibold text-lg"
                style={{ color: "white" }}
              >
              {agentNumber}
              </p>
              <p
                className="text-lg"
                style={{ color: "white" }}
              >
              {agentEmail}
              </p>
            </div>
          </div>
          {/* Right: About card with glassmorphism */}
          <div className="flex flex-col justify-center">
            <h1 className="text-4xl font-mont pb-8 font-bold">{constants.ABOUT_TITLE}</h1>
          <p style={{ color: "white" }}>{aboutText}</p>
          </div>
        </div>
        {/* Fade-in animation keyframes */}
        <style jsx>{`
        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(40px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      </section>
    </>
  );
}
